import type { LogoutRepository } from '@/domains/auth/logout.repository';
import type { WarningNotificationRepository } from '@/domains/ui/warningNotification.repository';

/**
 * Use Case para el proceso completo de cierre de sesión (logout) del usuario.
 *
 * Funcionalidad:
 * - Ejecuta el flujo completo de cierre de sesión del sistema
 * - Muestra notificaciones apropiadas según el motivo del logout
 * - Limpia el estado de autenticación y redirige al login
 * - Maneja diferentes escenarios de logout (manual, token expirado, token inválido)
 *
 * Responsabilidades:
 * - Determinar y mostrar el mensaje de notificación apropiado según el motivo
 * - Coordinar la ejecución del logout con el repositorio
 * - Limpiar datos de sesión del store y storage
 * - Redirigir al usuario a la página de login
 * - Actuar como intermediario entre dominio, infraestructura y UI
 */
export class LogoutUseCase {
  private warningNotification: WarningNotificationRepository;
  private logout: LogoutRepository;

  /**
   * Inicializa el UseCase con las dependencias necesarias.
   *
   * @param warningNotification - Repositorio para mostrar notificaciones de advertencia al usuario
   * @param logout - Repositorio que ejecuta la lógica de cierre de sesión
   */
  constructor(
    warningNotification: WarningNotificationRepository,
    logout: LogoutRepository,
  ) {
    this.warningNotification = warningNotification;
    this.logout = logout;
  }

  /**
   * Ejecuta el proceso completo de logout con notificación al usuario.
   *
   * @param reason - Motivo del logout: 'manual' (usuario cierra sesión), 'token_expired' (sesión expirada), 'token_invalid' (sesión inválida)
   */
  async execute(
    reason?: 'manual' | 'token_expired' | 'token_invalid',
  ): Promise<void> {
    const message = this.getLogoutMessage(reason);
    await this.warningNotification.showWarning(message);
    await this.logout.logout();
  }

  /**
   * Obtiene el mensaje de notificación apropiado según el motivo del logout.
   *
   * @param reason - Motivo del logout ('manual', 'token_expired', 'token_invalid')
   */
  private getLogoutMessage(
    reason?: 'manual' | 'token_expired' | 'token_invalid',
  ): string {
    switch (reason) {
      case 'token_expired':
        return 'Su sesión ha expirado. Será redirigido al login.';
      case 'token_invalid':
        return 'Su sesión no es válida. Será redirigido al login.';
      default:
        return 'Cerrando sesión...';
    }
  }
}
